// ============================================================
// TV INFRASTRUCTURE — Types (Fase 3)
// ============================================================

// ─── Dashboard slide ─────────────────────────────────────────
export type DashboardId =
  | 'operacional'
  | 'fornecedores'
  | 'seguranca'
  | 'efetivo'
  | 'producao'
  | 'avanco'
  | 'operational_intelligence'
  | 'camera_wall'
  | 'vision_risk'
  | 'digital_twin'

export interface TVSlide {
  id: string                // uuid local
  dashboardId: DashboardId
  durationSec: number
  enabled: boolean
  label?: string            // override do label padrão
}

// ─── TV Profile ───────────────────────────────────────────────
export interface TVProfile {
  id: string
  nome: string
  slides: TVSlide[]
  transitionSec: number     // duração da animação de transição
  tickerEnabled: boolean
  showClock: boolean
  showConnectionStatus: boolean
}

export const TV_PROFILE_DEFAULTS: Omit<TVProfile, 'id' | 'nome' | 'slides'> = {
  transitionSec: 0.6,
  tickerEnabled: true,
  showClock: true,
  showConnectionStatus: true,
}

// Perfis pré-definidos
export const TV_PROFILES: TVProfile[] = [
  {
    id: 'completo',
    nome: 'Completo',
    transitionSec: 0.6,
    tickerEnabled: true,
    showClock: true,
    showConnectionStatus: true,
    slides: [
      { id: 's1', dashboardId: 'operacional',  durationSec: 30, enabled: true },
      { id: 's2', dashboardId: 'fornecedores', durationSec: 20, enabled: true },
      { id: 's3', dashboardId: 'seguranca',    durationSec: 15, enabled: true },
      { id: 's4', dashboardId: 'efetivo',      durationSec: 15, enabled: true },
      { id: 's5', dashboardId: 'producao',     durationSec: 20, enabled: true },
    ],
  },
  {
    id: 'operacional',
    nome: 'Só Operacional',
    transitionSec: 0.5,
    tickerEnabled: true,
    showClock: true,
    showConnectionStatus: true,
    slides: [
      { id: 's1', dashboardId: 'operacional',  durationSec: 0, enabled: true }, // 0 = sem rotação
    ],
  },
  {
    id: 'intelligence',
    nome: 'Inteligência Operacional',
    transitionSec: 0.6,
    tickerEnabled: true,
    showClock: true,
    showConnectionStatus: true,
    slides: [
      { id: 's1', dashboardId: 'operational_intelligence', durationSec: 0, enabled: true },
    ],
  },
  // ── Fase 8.14 — Executive TV Profiles ──
  {
    id: 'executive',
    nome: 'Executivo Corporativo',
    transitionSec: 0.7,
    tickerEnabled: false,
    showClock: true,
    showConnectionStatus: true,
    slides: [
      { id: 's1', dashboardId: 'operational_intelligence', durationSec: 45, enabled: true },
      { id: 's2', dashboardId: 'operacional',              durationSec: 30, enabled: true },
      { id: 's3', dashboardId: 'seguranca',                durationSec: 25, enabled: true },
    ],
  },
  {
    id: 'war_room',
    nome: 'War Room — Modo Crise',
    transitionSec: 0.3,
    tickerEnabled: true,
    showClock: true,
    showConnectionStatus: true,
    slides: [
      { id: 's1', dashboardId: 'operacional',              durationSec: 20, enabled: true },
      { id: 's2', dashboardId: 'seguranca',                durationSec: 20, enabled: true },
      { id: 's3', dashboardId: 'operational_intelligence', durationSec: 20, enabled: true },
    ],
  },
  {
    id: 'corporate_ops',
    nome: 'Operações Corporativas',
    transitionSec: 0.5,
    tickerEnabled: true,
    showClock: true,
    showConnectionStatus: true,
    slides: [
      { id: 's1', dashboardId: 'operational_intelligence', durationSec: 40, enabled: true },
      { id: 's2', dashboardId: 'efetivo',                  durationSec: 25, enabled: true },
      { id: 's3', dashboardId: 'fornecedores',             durationSec: 25, enabled: true },
      { id: 's4', dashboardId: 'operacional',              durationSec: 30, enabled: true },
    ],
  },
  // ── Fase 9.16 — Vision TV Profiles ──
  {
    id: 'vision_safety',
    nome: 'Segurança Visual (Vision)',
    transitionSec: 0.4,
    tickerEnabled: true,
    showClock:     true,
    showConnectionStatus: true,
    slides: [
      { id: 's1', dashboardId: 'camera_wall', durationSec: 30, enabled: true },
      { id: 's2', dashboardId: 'vision_risk',  durationSec: 30, enabled: true },
      { id: 's3', dashboardId: 'seguranca',    durationSec: 20, enabled: true },
    ],
  },
  {
    id: 'directors',
    nome: 'Diretoria',
    transitionSec: 1.0,
    tickerEnabled: false,
    showClock: true,
    showConnectionStatus: false,
    slides: [
      { id: 's1', dashboardId: 'operational_intelligence', durationSec: 60, enabled: true },
      { id: 's2', dashboardId: 'seguranca',                durationSec: 30, enabled: true },
    ],
  },
  {
    id: 'seguranca',
    nome: 'Foco Segurança',
    transitionSec: 0.6,
    tickerEnabled: true,
    showClock: true,
    showConnectionStatus: true,
    slides: [
      { id: 's1', dashboardId: 'seguranca',    durationSec: 25, enabled: true },
      { id: 's2', dashboardId: 'operacional',  durationSec: 20, enabled: true },
      { id: 's3', dashboardId: 'efetivo',      durationSec: 15, enabled: true },
    ],
  },
]

// ─── TV State ─────────────────────────────────────────────────
export type TVConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'offline'
export type TVPlaybackStatus   = 'playing' | 'paused' | 'transitioning'

export interface TickerItem {
  id: string
  text: string
  type: 'info' | 'warning' | 'critical' | 'ok'
  timestamp: string
}

export interface TVState {
  // Configuração
  obraId: string | null
  companyId: string | null
  profile: TVProfile
  profileId: string

  // Playback
  currentSlideIndex: number
  playbackStatus: TVPlaybackStatus
  elapsedSec: number           // segundos no slide atual
  totalCycles: number          // quantas vezes a playlist completou

  // Conexão
  connectionStatus: TVConnectionStatus
  lastConnectedAt: string | null
  isOffline: boolean
  offlineSinceMs: number | null

  // Ticker
  tickerItems: TickerItem[]

  // UI
  isFullscreen: boolean
  wakeLockActive: boolean
  showControls: boolean        // hover overlay com controles
  lastInteractionMs: number

  // Actions
  setObraId: (id: string) => void
  setCompanyId: (id: string) => void
  setProfile: (profile: TVProfile) => void
  setProfileId: (id: string) => void
  nextSlide: () => void
  prevSlide: () => void
  goToSlide: (index: number) => void
  setPlaybackStatus: (s: TVPlaybackStatus) => void
  setElapsedSec: (n: number) => void
  incrementElapsed: () => void
  resetElapsed: () => void
  setConnectionStatus: (s: TVConnectionStatus) => void
  setIsOffline: (v: boolean) => void
  setIsFullscreen: (v: boolean) => void
  setWakeLockActive: (v: boolean) => void
  setShowControls: (v: boolean) => void
  touchInteraction: () => void
  addTickerItem: (item: TickerItem) => void
  clearTickerItems: () => void
  incrementCycles: () => void
}
